// 小画面（スマートフォン）向けの推奨環境オーバーレイ。
//
// ゲームは Phaser.Scale.FIT ＋ 設計解像度 1024×768 固定なので、画面が小さくなっても
// **レイアウトは崩れず全要素が等倍で縮むだけ**。つまりスマホでも「動く」が、牌が小さすぎて
// 実用的でない。そこを作り込む予定は無い（サポート外・2026-07-31 判断、2026-09-11 に再確認）ので、
// 初見の人が「壊れている」と誤解しないよう推奨環境を案内する。
//
// 💡 **実寸は1つの式で出る**（2026-09-11）。横向きの iPad はどれも 4:3 以上に横長なので
// FIT は必ず高さ側で決まり ⇒ **盤の1マス ＝ ビューポートの物理的な高さ × (マスの設計px / 768)**。
// ⚠️ だから「小さい画面だと読みづらい」は**連続的に効く**——どこかで急に壊れるわけではない。
//
// **遊ぶこと自体は妨げない**（「このまま続ける」で閉じられ、以後は二度と出ない）。

const DISMISS_KEY = 'hongsoft.smallScreenNoticeDismissed';

/**
 * 「小画面」とみなす短辺のしきい値（CSS px）。
 * スマホの短辺は 360〜430 程度、タブレットは iPad mini でも 744 あるので
 * 540 で綺麗に分かれる。`pointer: coarse` も併せて見るので、
 * PC でウィンドウを小さくしただけでは出ない。
 *
 * 🔴 **推奨（11インチ以上）より、このしきい値はわざと緩い**（2026-09-11）。
 *   推奨を下回る端末は iPad mini（8.3"）まで含むが、**mini に出すのはやり過ぎ**と判断した:
 *   実測で 10×10 のマスは **約 9.0mm**（iPad Air 11" の約 12.6mm に対し 71%）で、
 *   タップの下限(7mm)は超えている——⚠️ **先に効くのは読みやすさの方**（手がかりの字が 2.7mm）。
 *   ⇒ **割り込みで止めるのはスマホだけ**にして、推奨は
 *   ダッシュボードのフッター（`portal.env.note`）で**黙って明記する**。
 *   この2つは同じ基準を指しているので、⚠️ **どちらかを動かすときは必ず両方見る**。
 */
const SMALL_SIDE_PX = 540;

function isSmallTouchScreen(): boolean {
  if (!window.matchMedia('(pointer: coarse)').matches) return false;
  return Math.min(window.innerWidth, window.innerHeight) < SMALL_SIDE_PX;
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false; // プライベートモード等では毎回出る（実害なし）
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* 保存できなくても閉じられればよい */
  }
}

/**
 * 必要なら推奨環境オーバーレイを出す。条件を満たさなければ何もしない。
 * 起動時に一度だけ呼ぶ（回転で短辺が変わることはないので再評価しない）。
 */
export function maybeShowSmallScreenNotice(): void {
  if (isDismissed() || !isSmallTouchScreen()) return;

  const el = document.getElementById('smallscreen-notice');
  const btn = document.getElementById('smallscreen-ok');
  if (!el || !btn) return;

  el.style.display = 'flex';
  el.setAttribute('aria-hidden', 'false');
  btn.addEventListener(
    'click',
    () => {
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
      markDismissed();
    },
    { once: true },
  );
}
